/**
 * What a piece of feedback says about the device it came from: browser, OS and
 * a little hardware, so "the cards are cut off" can be read against the screen
 * that cut them.
 *
 * Two sources, best first:
 *
 * - **User-Agent Client Hints** (`navigator.userAgentData`), Chromium only. The
 *   one place an exact OS version and an Android phone's model still exist:
 *   Chrome's UA string now says `Android 10; K` and `Windows NT 10.0` whatever
 *   the truth, and Windows 11 can ONLY be told from 10 here.
 * - **The UA string** for everything else. Safari freezes it on purpose - an
 *   iPad asks for the desktop site and reads as a Mac, macOS is `10_15_7`
 *   forever - so on Apple devices some of this is the best guess there is.
 *
 * The parsing is pure (`parseUserAgent`, `fromClientHints`) so it can be tested
 * against real strings; `collectDevice` is the part that touches `navigator`.
 * Nothing here is sent anywhere until the player has seen it in the preview and
 * pressed Send.
 */

export interface DeviceInfo {
  browser: string;
  browserVersion: string;
  os: string;
  osVersion: string;
  deviceType: "phone" | "tablet" | "desktop";
  /** "" where the platform will not say (iPhones, any non-Chromium browser). */
  deviceModel: string;
  language: string;
  screenWidth: number;
  screenHeight: number;
  viewportWidth: number;
  viewportHeight: number;
  pixelRatio: number;
  touchPoints: number;
  orientation: string;
  /** Running as the home-screen app rather than in a browser tab. */
  standalone: boolean;
  /** 0 where the browser does not say (Safari, Firefox). */
  cpuCores: number;
  /** In GB, rounded down by the browser on purpose; 0 where it does not say. */
  memoryGb: number;
  /** The Network Information API's effective type, e.g. "4g"; "" where absent. */
  connection: string;
}

export interface ParsedUserAgent {
  browser: string;
  browserVersion: string;
  os: string;
  osVersion: string;
  deviceType: DeviceInfo["deviceType"];
  deviceModel: string;
}

const BROWSERS: readonly [RegExp, string][] = [
  // Order matters: every Chromium browser also says Chrome/ and Safari/, and
  // every iOS browser also says Safari/.
  [/EdgiOS\/([\d.]+)/, "Edge"],
  [/EdgA?\/([\d.]+)/, "Edge"],
  [/OPR\/([\d.]+)/, "Opera"],
  [/SamsungBrowser\/([\d.]+)/, "Samsung Internet"],
  [/FxiOS\/([\d.]+)/, "Firefox"],
  [/Firefox\/([\d.]+)/, "Firefox"],
  [/CriOS\/([\d.]+)/, "Chrome"],
  [/Chrome\/([\d.]+)/, "Chrome"],
  [/Version\/([\d.]+).*Safari\//, "Safari"],
];

export function parseUserAgent(ua: string, maxTouchPoints = 0): ParsedUserAgent {
  let browser = "Unknown";
  let browserVersion = "";
  for (const [re, name] of BROWSERS) {
    const m = re.exec(ua);
    if (m) {
      browser = name;
      browserVersion = m[1] ?? "";
      break;
    }
  }
  // An iPhone home-screen app has no Version/ token: it is still Safari's engine.
  if (browser === "Unknown" && /AppleWebKit/.test(ua) && /\((iPhone|iPad|iPod)/.test(ua)) browser = "Safari";

  let os = "Unknown";
  let osVersion = "";
  let deviceType: DeviceInfo["deviceType"] = "desktop";
  let deviceModel = "";
  let m: RegExpExecArray | null;
  if ((m = /\((iPhone|iPad|iPod)[^)]*?OS (\d+(?:_\d+)*)/.exec(ua))) {
    os = m[1] === "iPad" ? "iPadOS" : "iOS";
    osVersion = (m[2] ?? "").replace(/_/g, ".");
    deviceType = m[1] === "iPad" ? "tablet" : "phone";
    deviceModel = m[1] ?? "";
  } else if ((m = /Android (\d+(?:\.\d+)*)/.exec(ua))) {
    os = "Android";
    osVersion = m[1] ?? "";
    // Android phones say Mobile; tablets do not.
    deviceType = /Mobile/.test(ua) ? "phone" : "tablet";
    const model = /Android [\d.]+; ([^;)]+?)(?: Build\/[^;)]*)?\)/.exec(ua)?.[1]?.trim() ?? "";
    // Reduced UA strings say "K" instead of the model.
    deviceModel = model === "K" ? "" : model;
  } else if (/Macintosh/.test(ua) && maxTouchPoints > 1) {
    // An iPad asking for the desktop site: indistinguishable from a Mac except
    // that Macs have no touch screen. Its iPadOS version is not in the string.
    os = "iPadOS";
    deviceType = "tablet";
    deviceModel = "iPad";
  } else if ((m = /Mac OS X (\d+(?:[_.]\d+)*)/.exec(ua))) {
    os = "macOS";
    osVersion = (m[1] ?? "").replace(/_/g, ".");
  } else if ((m = /Windows NT (\d+\.\d+)/.exec(ua))) {
    os = "Windows";
    // NT 10.0 is 10 AND 11; only client hints can tell them apart.
    osVersion = ({ "10.0": "10", "6.3": "8.1", "6.2": "8", "6.1": "7" } as Record<string, string>)[m[1] ?? ""] ?? m[1] ?? "";
  } else if (/CrOS/.test(ua)) {
    os = "ChromeOS";
  } else if (/Linux/.test(ua)) {
    os = "Linux";
  }
  return { browser, browserVersion, os, osVersion, deviceType, deviceModel };
}

/** The high-entropy client hints this asks for, as `getHighEntropyValues` returns them. */
export interface ClientHints {
  platform?: string;
  platformVersion?: string;
  model?: string;
  mobile?: boolean;
  fullVersionList?: { brand: string; version: string }[];
}

/**
 * Corrects a UA parse with client hints, field by field, only where the hints
 * know better. Windows 11 is platformVersion 13 and up (Microsoft's documented
 * mapping); 1-10 is Windows 10.
 */
export function fromClientHints(parsed: ParsedUserAgent, hints: ClientHints): ParsedUserAgent {
  const out = { ...parsed };
  const pv = hints.platformVersion ?? "";
  if (hints.platform === "Windows" && pv) {
    const major = Number(pv.split(".")[0]);
    out.os = "Windows";
    out.osVersion = major >= 13 ? "11" : major > 0 ? "10" : out.osVersion;
  } else if (pv && (hints.platform === "Android" || hints.platform === "macOS" || hints.platform === "Chrome OS")) {
    out.osVersion = pv;
  }
  if (hints.model) out.deviceModel = hints.model;
  // The brand list carries the full version (the UA string's minor parts are
  // zeroed); take the entry for the browser already identified.
  const brandName = out.browser === "Edge" ? "Microsoft Edge" : out.browser === "Chrome" ? "Google Chrome" : out.browser;
  const full = hints.fullVersionList?.find((b) => b.brand === brandName);
  if (full) out.browserVersion = full.version;
  return out;
}

interface NavigatorWithExtras extends Navigator {
  userAgentData?: { getHighEntropyValues(hints: string[]): Promise<ClientHints> };
  deviceMemory?: number;
  connection?: { effectiveType?: string };
  standalone?: boolean;
}

/** Everything above, read off this browser. Never rejects: a hint that fails is a hint left out. */
export async function collectDevice(): Promise<DeviceInfo> {
  const nav = navigator as NavigatorWithExtras;
  let parsed = parseUserAgent(nav.userAgent, nav.maxTouchPoints ?? 0);
  if (nav.userAgentData) {
    try {
      const hints = await nav.userAgentData.getHighEntropyValues(["platform", "platformVersion", "model", "fullVersionList"]);
      parsed = fromClientHints(parsed, hints);
    } catch {
      // Keep what the UA string said.
    }
  }
  const standalone =
    nav.standalone === true || (typeof matchMedia === "function" && matchMedia("(display-mode: standalone)").matches);
  return {
    ...parsed,
    language: nav.language ?? "",
    screenWidth: screen.width,
    screenHeight: screen.height,
    viewportWidth: window.innerWidth,
    viewportHeight: window.innerHeight,
    pixelRatio: Math.round((window.devicePixelRatio || 1) * 100) / 100,
    touchPoints: nav.maxTouchPoints ?? 0,
    // The window's shape, which is what the layout answers to. screen.orientation
    // is the PANEL's, and disagrees with it in a split screen or a desktop window.
    orientation: window.innerWidth > window.innerHeight ? "landscape" : "portrait",
    standalone,
    cpuCores: nav.hardwareConcurrency ?? 0,
    memoryGb: nav.deviceMemory ?? 0,
    connection: nav.connection?.effectiveType ?? "",
  };
}
